import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { HeroSection } from "@/components/HeroSection";
import { TrustSection } from "@/components/TrustSection";
import { BeforeAfterSlider } from "@/components/BeforeAfterSlider";
import { ArrowLeft, ArrowRight, Instagram, Heart, Hammer, Facebook } from "lucide-react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { isRTL, useLanguageStore } from "@/stores/languageStore";
import pageBgVideo from "@/assets/page-bg.mp4";
import carpentryHero from "@/assets/carpentry/outdoor-table-2.jpg";
import carpentryImg2 from "@/assets/carpentry/tv-cabinet.jpg";
import carpentryImg3 from "@/assets/carpentry/vanity-cabinet.jpg";
import carpentryImg4 from "@/assets/carpentry/coffee-table.jpg";
import carpentryImg5 from "@/assets/carpentry/glass-cabinet.jpg";
import carpentryImg6 from "@/assets/carpentry/console-table.jpg";
import tableRestoration from "@/assets/carpentry/table-restoration.jpg";
import woodShelf from "@/assets/carpentry/wood-shelf.jpg";
import outdoorTable3 from "@/assets/carpentry/outdoor-table-3.jpg";
import outdoorTable4 from "@/assets/carpentry/outdoor-table-4.jpg";
import deckBefore from "@/assets/carpentry/deck-before.jpg";
import deckAfter from "@/assets/carpentry/deck-after.jpg";

import { INSTAGRAM_URL, FACEBOOK_URL } from "@/lib/socialLinks";

const Index = () => {
  const { t, language } = useLanguageStore();
  const rtl = isRTL(language);
  const ArrowIcon = rtl ? ArrowLeft : ArrowRight;
  const arrowSpacing = rtl ? "mr-2" : "ml-2";

  const tags = [t("home.tag1"), t("home.tag2"), t("home.tag3"), t("home.tag4")];

  const bentoItems = [
    { src: carpentryHero, label: t("home.labelOutdoorTable"), span: "col-span-2" },
    { src: carpentryImg2, label: t("home.labelTvCabinet"), span: "" },
    { src: carpentryImg3, label: t("home.labelVanity"), span: "" },
    { src: woodShelf, label: t("home.labelShelf"), span: "" },
    { src: carpentryImg4, label: t("home.labelCoffeeTable"), span: "" },
    { src: carpentryImg5, label: t("home.labelGlassCabinet"), span: "" },
    { src: carpentryImg6, label: t("home.labelConsole"), span: "" },
    { src: outdoorTable4, label: t("home.labelOutdoorTable"), span: "col-span-2" },
  ];

  return (
    <div className="min-h-screen flex flex-col overflow-x-hidden">
      <Header />

      <main className="flex-1 overflow-x-hidden">
        <HeroSection />

        {/* Top Contact Banner */}
        <section className="py-6 bg-charcoal relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-primary/5 via-gold/10 to-primary/5" />
          <div className="absolute inset-0 opacity-30">
            <div className="absolute top-0 left-1/4 w-32 h-32 bg-primary/20 rounded-full blur-3xl" />
            <div className="absolute bottom-0 right-1/4 w-40 h-40 bg-gold/20 rounded-full blur-3xl" />
          </div>
          <div className="container relative z-10">
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8"
            >
              <div className="flex items-center gap-4">
                <motion.div 
                  animate={{ scale: [1, 1.05, 1] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                  className="w-12 h-12 rounded-full bg-gradient-to-br from-primary via-primary to-gold/80 flex items-center justify-center shadow-xl ring-2 ring-primary/30 ring-offset-2 ring-offset-charcoal"
                >
                  <Heart className="h-6 w-6 text-white fill-white/30" />
                </motion.div>
                <div className="flex flex-col">
                  <span className="text-gold text-sm font-medium">{t("home.contactComfort")}</span>
                  <span className="text-white text-lg md:text-xl font-display font-semibold">{t("home.contactAvailable")}</span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <motion.a 
                  href={INSTAGRAM_URL}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.98 }}
                  className="inline-flex items-center gap-3 px-7 py-3 rounded-full gradient-gold text-white shadow-elegant font-bold text-lg group"
                >
                  <Instagram className="h-5 w-5 group-hover:animate-pulse" />
                  <span>{t("home.contactCta")}</span>
                </motion.a>
                <motion.a
                  href={FACEBOOK_URL}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.98 }}
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#1877F2] text-white shadow-lg font-bold text-lg group"
                >
                  <Facebook className="h-5 w-5 group-hover:animate-pulse" />
                </motion.a>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Carpentry Showcase — Premium Gallery */}
        <section className="py-24 bg-charcoal relative overflow-hidden">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-0 left-1/4 w-64 h-64 bg-primary/30 rounded-full blur-[120px]" />
            <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-gold/20 rounded-full blur-[120px]" />
            <div className="absolute top-1/2 right-1/3 w-40 h-40 bg-primary/20 rounded-full blur-[100px]" />
          </div>
          <div className="container relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <motion.div
                animate={{ rotate: [0, 5, -5, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary/20 to-gold/20 border border-primary/30 flex items-center justify-center mx-auto mb-6 shadow-xl"
              >
                <Hammer className="w-8 h-8 text-primary" />
              </motion.div>
              <span className="inline-block px-5 py-2 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm font-semibold tracking-wide mb-4">
                {t("home.portfolioBadge")}
              </span>
              <h2 className="font-display text-3xl md:text-5xl font-bold text-white mb-4">
                {t("home.carpentryTitle")} <span className="text-primary">{t("home.carpentryAccent")}</span>
              </h2>
              <p className="text-white/60 text-lg max-w-2xl mx-auto leading-relaxed">
                {t("home.carpentryDesc")}
                <br className="hidden md:block" />
                <span className="text-primary/80">{t("home.carpentryDescAccent")}</span>
              </p>
            </motion.div>

            {/* Feature Tags */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="flex flex-wrap justify-center gap-3 mb-12"
            >
              {tags.map((tag, i) => (
                <motion.span
                  key={tag}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="px-4 py-2 rounded-full bg-white/5 border border-white/10 backdrop-blur-sm text-white/80 text-sm font-medium"
                >
                  {tag}
                </motion.span>
              ))}
            </motion.div>

            {/* Bento Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-12">
              {bentoItems.map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.08 }}
                  className={`group relative overflow-hidden rounded-2xl ${item.span} aspect-[4/3]`}
                >
                  <img
                    src={item.src}
                    alt={item.label}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                  <div className="absolute inset-0 bg-gradient-to-br from-white/0 via-white/5 to-white/0 opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                  <div className="absolute bottom-3 right-3 md:bottom-4 md:right-4">
                    <span className="px-3 py-1.5 rounded-full bg-black/40 backdrop-blur-md text-white text-xs md:text-sm font-medium border border-white/10 group-hover:bg-primary/70 group-hover:border-primary/40 transition-all duration-500">
                      {item.label}
                    </span>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Interactive Before/After Slider */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mb-12"
            >
              <h3 className="text-center text-xl md:text-2xl font-display font-bold text-white mb-8">
                {t("home.beforeAfterTitle")} <span className="text-primary">{t("home.beforeAfterAccent")}</span>
              </h3>

              <div className="max-w-3xl mx-auto space-y-8">
                <div>
                  <h4 className="text-center text-lg font-display font-semibold text-white/80 mb-4">{t("home.tableRenovation")}</h4>
                  <BeforeAfterSlider
                    beforeSrc={tableRestoration}
                    afterSrc={outdoorTable3}
                  />
                </div>

                <div>
                  <h4 className="text-center text-lg font-display font-semibold text-white/80 mb-4">{t("home.deckRenovation")}</h4>
                  <BeforeAfterSlider
                    beforeSrc={deckBefore}
                    afterSrc={deckAfter}
                  />
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="flex flex-col sm:flex-row items-center justify-center gap-4"
            >
              <Link to="/lp/nagarut">
                <Button size="lg" className="gradient-gold text-white px-10 py-7 text-lg font-bold shadow-2xl hover:scale-105 transition-all duration-300">
                  {t("home.viewAllWorks")}
                  <ArrowIcon className={`${arrowSpacing} h-5 w-5`} />
                </Button>
              </Link>
              <div className="flex items-center gap-3">
                <motion.a
                  href={INSTAGRAM_URL}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.98 }}
                  className="inline-flex items-center gap-3 px-8 py-4 rounded-full border border-white/20 bg-white/5 backdrop-blur-md text-white font-bold text-lg hover:bg-white/10 transition-all"
                >
                  <Instagram className="h-5 w-5 text-primary" />
                  <span>{t("home.contactCta")}</span>
                </motion.a>
                <motion.a
                  href={FACEBOOK_URL}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.98 }}
                  className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#1877F2] text-white font-bold hover:bg-[#166FE5] transition-all"
                >
                  <Facebook className="h-5 w-5" />
                </motion.a>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Contact Comfort Section */}
        <section className="py-12 bg-charcoal relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-gold/5 via-primary/10 to-gold/5" />
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-1/2 left-10 w-48 h-48 bg-primary/30 rounded-full blur-3xl" />
            <div className="absolute top-1/2 right-10 w-56 h-56 bg-gold/30 rounded-full blur-3xl" />
          </div>
          <div className="container relative z-10">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="flex flex-col md:flex-row items-center justify-center gap-6 md:gap-12"
            >
              <div className="flex items-center gap-5">
                <motion.div 
                  animate={{ scale: [1, 1.08, 1] }}
                  transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
                  className="w-16 h-16 rounded-full bg-gradient-to-br from-primary via-primary to-gold/80 flex items-center justify-center shadow-2xl ring-4 ring-primary/20 ring-offset-4 ring-offset-charcoal"
                >
                  <Heart className="h-8 w-8 text-white fill-white/30" />
                </motion.div>
                <div className="flex flex-col">
                  <span className="text-gold text-base font-medium">{t("home.contactComfort")}</span>
                  <span className="text-white text-2xl md:text-3xl font-display font-bold">{t("home.contactAvailable")}</span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <motion.a 
                  href={INSTAGRAM_URL}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.98 }}
                  className="inline-flex items-center gap-4 px-10 py-4 rounded-full gradient-gold text-white shadow-elegant font-bold text-xl group"
                >
                  <Instagram className="h-6 w-6 group-hover:animate-pulse" />
                  <span>{t("home.contactCta")}</span>
                </motion.a>
                <motion.a
                  href={FACEBOOK_URL}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.98 }}
                  className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#1877F2] text-white shadow-lg font-bold group"
                >
                  <Facebook className="h-6 w-6 group-hover:animate-pulse" />
                </motion.a>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Trust Section */}
        <TrustSection />

        {/* CTA Section */}
        <section className="py-20 relative overflow-hidden text-white">
          <video
            autoPlay
            loop
            muted
            playsInline
            className="absolute inset-0 w-full h-full object-cover"
          >
            <source src={pageBgVideo} type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-charcoal/60" />
          <div className="container text-center relative z-10">
            <h2 className="font-display text-3xl md:text-4xl font-bold mb-4">{t("home.ctaTitle")}</h2>
            <p className="text-white/70 max-w-2xl mx-auto mb-8">{t("home.ctaDescription")}</p>
            <div className="flex items-center justify-center gap-4">
              <motion.a
                href={INSTAGRAM_URL}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                className="inline-flex items-center gap-3 px-10 py-4 rounded-full gradient-gold text-white shadow-elegant font-bold text-xl"
              >
                <Instagram className="h-6 w-6" />
                {t("home.ctaButton")}
              </motion.a>
              <motion.a
                href={FACEBOOK_URL}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#1877F2] text-white shadow-lg font-bold"
              >
                <Facebook className="h-6 w-6" />
              </motion.a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Index;
